import type { JSX } from "react";
import { Provider } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { AnyAction, Store } from "redux";
import { Persistor } from "redux-persist";
import { PersistPartial } from "redux-persist/es/persistReducer";
import { PersistGate } from "redux-persist/integration/react";
import Loader from "../components/atoms/Loader";
import { LearningMaterialFilterWithLocalStorageProvider } from "../components/features/LearningMaterialFilter/useLearningMaterialFilterWithLocalStorageContext";
import { StudentFilterProvider } from "../components/features/NewStudentFilter/useStudentFilterContext";
import { DrawerProvider } from "../components/general/Drawer/useDrawerContext";
import { FSErrorBoundary } from "../components/general/ErrorBoundary";
import { FieldVisibilityToggleProvider } from "../components/general/FieldVisibilityToggle/useFieldVisibilityToggleContext";
import FlashMessage from "../components/organisms/FlashMessage";
import WindowReload from "../components/organisms/WindowReload";
import { NavigationProvider } from "../contexts/NavigationContext";
import { SingleDateFilterProvider } from "../contexts/SingleDateFilterContext";
import InvitationsPage from "../features/InvitationsPage";
import { MultiSectionBatchesDetailPage } from "../features/MultiSectionBatchesDetailPage";
import { MultiSectionBatchesDownloadAiQuestionCountsPage } from "../features/MultiSectionBatchesDownloadAiQuestionCountsPage";
import { MultiSectionBatchesDownloadAiQuestionDetailsPage } from "../features/MultiSectionBatchesDownloadAiQuestionDetailsPage";
import { MultiSectionBatchesDownloadExaminationResultsPage } from "../features/MultiSectionBatchesDownloadExaminationResultsPage";
import { MultiSectionBatchesDownloadKartesPage } from "../features/MultiSectionBatchesDownloadKartesPage";
import { MultiSectionBatchesDownloadSectionUsageRatesPage } from "../features/MultiSectionBatchesDownloadSectionUsageRatesPage";
import { MultiSectionBatchesDownloadStaysPage } from "../features/MultiSectionBatchesDownloadStaysPage";
import { MultiSectionBatchesDownloadStudentsPage } from "../features/MultiSectionBatchesDownloadStudentsPage";
import { MultiSectionBatchesDownloadStudyRecordsPage } from "../features/MultiSectionBatchesDownloadStudyRecordsPage";
import { MultiSectionBatchesPage } from "../features/MultiSectionBatchesPage";
import { MultiSectionBatchesUpdateDrillPage } from "../features/MultiSectionBatchesUpdateDrillPage";
import {
  MultiSectionBatchesUploadDrillHistoryPage,
  MultiSectionBatchesUploadDrillListPage,
  MultiSectionBatchesUploadDrillPage,
} from "../features/MultiSectionBatchesUploadDrillPage";
import PasswordResetPage from "../features/PasswordResetPage";
import { SectionsAnalyticsStudyTaskDetailPage } from "../features/SectionAnalyticsStudyTaskDetailPage";
import SectionStudyRecordCommentsPage from "../features/SectionStudyRecordCommentsPage";
import { SectionsAnalyticsDrillSectionDetailPage } from "../features/SectionsAnalyticsDrillSectionDetailPage";
import { SectionsAnalyticsExaminationDetailPage } from "../features/SectionsAnalyticsExaminationDetailPage";
import { SectionsAnalyticsExaminationPage } from "../features/SectionsAnalyticsExaminationPage/SectionsAnalyticsExaminationPage";
import { SectionsAnalyticsLectureAttendancePage } from "../features/SectionsAnalyticsLectureAttendancePage";
import { SectionsAnalyticsLectureStudentPage } from "../features/SectionsAnalyticsLectureStudentPage";
import { SectionsAnalyticsRetentionLevelDetailPage } from "../features/SectionsAnalyticsRetentionLevelDetailPage/SectionsAnalyticsRetentionLevelDetailPage";
import { SectionsAnalyticsRetentionLevelPage } from "../features/SectionsAnalyticsRetentionLevelPage/SectionsAnalyticsRetentionLevelPage";
import { SectionsAnalyticsStudyTaskNewPage } from "../features/SectionsAnalyticsStudyTaskNewPage";
import { SectionsAnalyticsStudyTaskPage } from "../features/SectionsAnalyticsStudyTaskPage";
import { SectionAnalyticsStudyTaskTabsProvider } from "../features/SectionsAnalyticsStudyTaskPage/useStudyTaskSwitchTabs";
import SectionsAnnounceMessagesPage from "../features/SectionsAnnounceMessagesPage";
import SectionsGuardianMessageThreadsPage from "../features/SectionsGuardianMessageThreadsPage/SectionsGuardianMessageThreadsPage";
import SectionsKartesPage from "../features/SectionsKartesPage";
import SectionsLearningCoachApplicationRequestsPage from "../features/SectionsLearningCoachApplicationRequestsPage";
import { SectionsLectureSessionDetailPage } from "../features/SectionsLectureSessionDetailPage";
import { SectionsLectureTransfersNewPage } from "../features/SectionsLectureTransfersNewPage/SectionsLectureTransfersNewPage";
import SectionsMessageThreadsPage from "../features/SectionsMessageThreadsPage";
import SectionsSettingsAiAssistantTemplatesEditPage from "../features/SectionsSettingsAiAssistantTemplatesEditPage";
import SectionsSettingsAiAssistantTemplatesNewPage from "../features/SectionsSettingsAiAssistantTemplatesNewPage";
import SectionsSettingsAiAssistantTemplatesPage from "../features/SectionsSettingsAiAssistantTemplatesPage";
import { SectionsSettingsClassroomPage } from "../features/SectionsSettingsClassroomsPage";
import { SectionsSettingsDigitalLearningMaterialsNewPage } from "../features/SectionsSettingsDigitalLearningMaterialsNewPage";
import { SectionsSettingsDigitalLearningMaterialsPage } from "../features/SectionsSettingsDigitalLearningMaterialsPage";
import SectionsSettingsKarteTemplatesEditPage from "../features/SectionsSettingsKarteTemplatesEditPage";
import SectionsSettingsKarteTemplatesNewPage from "../features/SectionsSettingsKarteTemplatesNewPage";
import SectionsSettingsKarteTemplatesPage from "../features/SectionsSettingsKarteTemplatesPage";
import { SectionsSettingsLecturesFormPage } from "../features/SectionsSettingsLecturesFormPage";
import { SectionsSettingsLecturesPage } from "../features/SectionsSettingsLecturesPage/SectionsSettingsLecturesPage";
import SectionsSettingsOperatorsPage from "../features/SectionsSettingsOperatorsPage";
import { SectionsSettingsSectionScheduleListPage } from "../features/SectionsSettingsSectionScheduleListPage";
import SectionsSettingsSkillMastersPage from "../features/SectionsSettingsSkillMastersPage";
import SectionsSettingsSkillsEditPage from "../features/SectionsSettingsSkillsEditPage";
import SectionsSettingsSkillsNewPage from "../features/SectionsSettingsSkillsNewPage";
import SectionsSettingsSkillsPage from "../features/SectionsSettingsSkillsPage";
import { SectionsSettingsStayingStudentsPage } from "../features/SectionsSettingsStayingStudentsPage";
import { SectionsSettingsStaysDetailsPage } from "../features/SectionsSettingsStaysDetailsPage/index";
import { SectionsSettingsStaysPage } from "../features/SectionsSettingsStaysPage";
import SectionsSettingsStudentsPage from "../features/SectionsSettingsStudentsPage/SectionsSettingsStudentsPage";
import { SectionsSettingsTeachersPage } from "../features/SectionsSettingsTeachersPage/index";
import SectionsStaffBoardPostEditPage from "../features/SectionsStaffBoardPostEditPage";
import SectionsStaffBoardPostPage from "../features/SectionsStaffBoardPostPage";
import SectionsStaffBoardPostsPage from "../features/SectionsStaffBoardPostsPage";
import SectionsStudentFollowPromptPage from "../features/SectionsStudentFollowPromptPage";
import SectionsStudentFollowSummariesPage from "../features/SectionsStudentFollowSummariesPage";
import { SectionsTimelineExaminationPage } from "../features/SectionsTimelineExaminationPage";
import SectionsTimelinePage from "../features/SectionsTimelinePage";
import SectionsTimelineSummaryPage from "../features/SectionsTimelineSummaryPage";
import StudentsAnalyticsAmountPage from "../features/StudentsAnalyticsAmountPage";
import { StudentsAnalyticsExaminationPage } from "../features/StudentsAnalyticsExaminationPage";
import { StudentAnalyticsExaminationResultPage } from "../features/StudentsAnalyticsExaminationResultPage/StudentsAnalyticsExaminationResultPage";
import { StudentsAnalyticsLectureAttendancePage } from "../features/StudentsAnalyticsLectureAttendancePage";
import { StudentsAnalyticsRetentionLevelPage } from "../features/StudentsAnalyticsRetentionLevelPage";
import StudentsAnalyticsTimePage from "../features/StudentsAnalyticsTimePage";
import { StudentsExaminationResultEditPage } from "../features/StudentsExaminationResultEditPage/StudentsExaminationResultEditPage";
import { StudentsExaminationResultsNewPage } from "../features/StudentsExaminationResultsNewPage/StudentsExaminationResultsNewPage";
import StudentsGuardianMessagesPage from "../features/StudentsGuardianMessagesPage/StudentsGuardianMessagesPage";
import StudentsKarteEditPage from "../features/StudentsKarteEditPage";
import StudentsKartePage from "../features/StudentsKartePage";
import StudentsKartesPage from "../features/StudentsKartesPage";
import StudentsLearningMaterialsNewPage from "../features/StudentsLearningMaterialsNewPage";
import StudentsLearningMaterialsPreRegistrationsNewPage from "../features/StudentsLearningMaterialsPreRegistrationsNewPage";
import StudentsLearningMaterialsPreRegistrationsPage from "../features/StudentsLearningMaterialsPreRegistrationsPage";
import StudentsMessagesPage from "../features/StudentsMessagesPage";
import { StudentsStayAnalyticsPage } from "../features/StudentsStayAnalyticsPage";
import StudentsStudyRecordPage from "../features/StudentsStudyRecordPage";
import StudentsTimelinePage from "../features/StudentsTimelinePage";
import AppStateInterface from "../interfaces/AppStateInterface";
import { composeProviders } from "./composeProviders";
import ErrorTracking from "./ErrorTracking";
import IntercomContainer from "./IntercomContainer";
import LoginCallback from "./LoginCallback";
import NoRouteMatch from "./NoRouteMatch";
import ActivationPage from "./pages/ActivationPage";
import ConsentPage from "./pages/Consent/index";
import EnglishPrivacyPolicyPage from "./pages/EnglishPrivacyPolicyPage";
import LoginPage from "./pages/LoginPage";
import PasswordResetRequestPage from "./pages/PasswordResetRequestPage";
import PasswordResetSentPage from "./pages/PasswordResetSentPage";
import { SectionSettingsEditContentFiles } from "./pages/SectionSettingsEditContentFiles";
import { SectionsSettingsLearningMaterialTags } from "./pages/SectionSettingsLearningMaterialTags";
import { SectionSettingsNewContentFiles } from "./pages/SectionSettingsNewContentFiles";
import SectionsAnalyticsAmountPage from "./pages/SectionsAnalyticsAmountPage";
import SectionsAnalyticsAnnounceMessagePage from "./pages/SectionsAnalyticsAnnounceMessagePage";
import SectionsAnalyticsDirectMessagePage from "./pages/SectionsAnalyticsDirectMessagePage";
import SectionsAnalyticsStayPage from "./pages/SectionsAnalyticsStayPage";
import SectionsAnalyticsTimePage from "./pages/SectionsAnalyticsTimePage";
import SectionsDashboardPage from "./pages/SectionsDashboardPage";
import SectionsGuardianAnnounceMessagesPage from "./pages/SectionsGuardianAnnounceMessagesPage";
import SectionsPage from "./pages/SectionsPage";
import { SectionsSettingsClassroomFormPage } from "./pages/SectionsSettingsClassroomFormPage";
import SectionsSettingsContentContentsResultsPage from "./pages/SectionsSettingsContentContentsResultsPage";
import { SectionsSettingsContentCourseDetail } from "./pages/SectionsSettingsContentCourseDetail";
import { SectionsSettingsContentCourseStudentsPage } from "./pages/SectionsSettingsContentCourseStudentsPage";
import { SectionsSettingsContentCoursesNewOrEditPage } from "./pages/SectionsSettingsContentCoursesNewOrEditPage/index";
import { SectionsSettingsContentCoursesPage } from "./pages/SectionsSettingsContentCoursesPage";
import SectionsSettingsContentCoursesResultsPage from "./pages/SectionsSettingsContentCoursesResultsPage";
import { SectionsSettingsContentCoursesSharePage } from "./pages/SectionsSettingsContentCoursesSharePage";
import { SectionsSettingsContentsPage } from "./pages/SectionsSettingsContentsPage";
import SectionsSettingsContentUnitsResultsPage from "./pages/SectionsSettingsContentUnitsResultsPage";
import { SectionsSettingsContentVideosNewOrEditPage } from "./pages/SectionsSettingsContentVideosNewOrEditPage";
import SectionsSettingsGoogleClassroomPage from "./pages/SectionsSettingsGoogleClassroomPage";
import SectionsSettingsGuardianMessagePage from "./pages/SectionsSettingsGuardianMessagePage";
import SectionsSettingsImportHistoriesOperationsPage from "./pages/SectionsSettingsImportHistoriesOperationsPage";
import SectionsSettingsImportHistoriesPage from "./pages/SectionsSettingsImportHistoriesPage";
import SectionsSettingsImportHistoryPage from "./pages/SectionsSettingsImportHistoryPage";
import SectionsSettingsImportOperationPage from "./pages/SectionsSettingsImportOperationPage";
import SectionsSettingsManagementLearningMaterialsPage from "./pages/SectionsSettingsManagementLearningMaterialsPage";
import SectionsSettingsSectionLearningMaterialsFormPage from "./pages/SectionsSettingsSectionLearningMaterialFormPage";
import SectionsSettingsSectionLearningMaterialsPage from "./pages/SectionsSettingsSectionLearningMaterialsPage";
import SectionsSettingsStudentsNewPage from "./pages/SectionsSettingsStudentsNewPage";
import SectionsSettingsTagsPage from "./pages/SectionsSettingsTagsPage";
import { SectionsSettingsTeacherNewPage } from "./pages/SectionsSettingsTeacherFormPage";
import SectionsSettingsThirdPartyApplicationsPage from "./pages/SectionsSettingsThirdPartyApplicationsPage";
import { SectionsStudySchedules } from "./pages/SectionsStudySchedules";
import SessionsEditPage from "./pages/SessionsEditPage";
import SettingsEmailPage from "./pages/SettingsEmailPage";
import SettingsProfilePage from "./pages/SettingsProfilePage";
import StudentCourseResultPage from "./pages/StudentCourseResultPage";
import { StudentsCalendar } from "./pages/StudentsCalendar";
import StudentsLearningMaterialsPage from "./pages/StudentsLearningMaterialsPage";
import { StudentsLearningMaterialTagAnalyticsTimePage } from "./pages/StudentsLearningMaterialTagAnalyticsTimePage";
import { LearningMaterialFilterProvider } from "./pages/StudentsLearningMaterialTagAnalyticsTimePage/useLearningMaterialFilterContext";
import StudentsStudySchedulesPage from "./pages/StudentsStudySchedulesPage";
import RequireLogin from "./Routes/RequireLogin";
import SectionLayout from "./Routes/SectionLayout/SectionLayout";
import SectionSettingLayout from "./Routes/SectionSettingLayout/SectionSettingLayout";
import StudentLayout from "./Routes/StudentsLayout/StudentLayout";
import Tracker from "./Tracker";

interface Props {
  store: Store<AppStateInterface & PersistPartial, AnyAction>;
  persistor: Persistor;
}

export const FSRoutes = (): JSX.Element => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    {/* NOTE: プライバシーポリシーの英語ページ。Zoomアプリ申請用に必要になった */}
    <Route path="/en/privacy_policy" element={<EnglishPrivacyPolicyPage />} />
    <Route path="/consent" element={<ConsentPage />} />
    <Route
      path="/activations/:emailActivationToken"
      element={<ActivationPage />}
    />
    <Route path="/password_reset" element={<PasswordResetRequestPage />} />
    <Route path="/password_reset/sent" element={<PasswordResetSentPage />} />
    <Route
      path="/password_reset/:password_reset_token"
      element={<PasswordResetPage />}
    />
    <Route element={<RequireLogin />}>
      {/* NOTE: ログインしているときのみ表示できるコンポーネントはここへ */}
      <Route path="/" element={<SectionsPage />} />

      {/* 校舎横断操作 */}
      <Route
        path="/multi_section_batches"
        element={<MultiSectionBatchesPage />}
      />
      <Route
        path="/multi_section_batches/download_study_records"
        element={<MultiSectionBatchesDownloadStudyRecordsPage />}
      />
      <Route
        path="/multi_section_batches/:batchId"
        element={<MultiSectionBatchesDetailPage />}
      />
      <Route
        path="/multi_section_batches/download_stays"
        element={<MultiSectionBatchesDownloadStaysPage />}
      />
      <Route
        path="/multi_section_batches/download_kartes"
        element={<MultiSectionBatchesDownloadKartesPage />}
      />
      <Route
        path="/multi_section_batches/download_students"
        element={<MultiSectionBatchesDownloadStudentsPage />}
      />
      <Route
        path="/multi_section_batches/download_examination_results"
        element={<MultiSectionBatchesDownloadExaminationResultsPage />}
      />
      <Route
        path="/multi_section_batches/download_section_usage_rates"
        element={<MultiSectionBatchesDownloadSectionUsageRatesPage />}
      />
      <Route
        path="/multi_section_batches/download_ai_question_details"
        element={<MultiSectionBatchesDownloadAiQuestionDetailsPage />}
      />
      <Route
        path="/multi_section_batches/download_ai_question_counts"
        element={<MultiSectionBatchesDownloadAiQuestionCountsPage />}
      />
      <Route
        path="/multi_section_batches/upload_drill"
        element={<MultiSectionBatchesUploadDrillPage />}
      />
      <Route
        path="/multi_section_batches/upload_drill/list"
        element={<MultiSectionBatchesUploadDrillListPage />}
      />
      <Route
        path="/multi_section_batches/upload_drill/history"
        element={<MultiSectionBatchesUploadDrillHistoryPage />}
      />
      <Route
        path="/multi_section_batches/update_drill/:materialCode"
        element={<MultiSectionBatchesUpdateDrillPage />}
      />

      {/* sessions */}
      <Route path="/sessions/edit" element={<SessionsEditPage />} />
      <Route path="/invitations/:invitationId" element={<InvitationsPage />} />
      {/* sections/ */}
      <Route path="/sections" element={<SectionsPage />} />
      <Route element={<SectionLayout />}>
        <Route
          path="/sections/:sectionId"
          element={<SectionsDashboardPage />}
        />
        <Route
          path="/sections/:sectionId/timeline/daily"
          element={<SectionsTimelineSummaryPage />}
        />
        <Route
          path="/sections/:sectionId/timeline/examination"
          element={<SectionsTimelineExaminationPage />}
        />
        <Route
          path="/sections/:sectionId/timeline"
          element={<SectionsTimelinePage />}
        />
        <Route
          path="/sections/:sectionId/analytics"
          element={<SectionsAnalyticsTimePage />}
        />
        <Route
          path="/sections/:sectionId/analytics/time"
          element={<SectionsAnalyticsTimePage />}
        />
        <Route
          path="/sections/:sectionId/analytics/amount"
          element={<SectionsAnalyticsAmountPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/stay"
          element={<SectionsAnalyticsStayPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/lecture_attendance/:lectureAttendanceAnalyticsType"
          element={<SectionsAnalyticsLectureAttendancePage />}
        />
        <Route
          path="/sections/:sectionId/analytics/lectures/:lectureId/students"
          element={<SectionsAnalyticsLectureStudentPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/direct_messages"
          element={<SectionsAnalyticsDirectMessagePage />}
        />
        <Route
          path="/sections/:sectionId/analytics/announce_messages"
          element={<SectionsAnalyticsAnnounceMessagePage />}
        />
        <Route
          path="/sections/:sectionId/analytics/examinations"
          element={<SectionsAnalyticsExaminationPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/examinations/:examinationId"
          element={<SectionsAnalyticsExaminationDetailPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/retention_level"
          element={<SectionsAnalyticsRetentionLevelPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/retention_level/:materialCode"
          element={<SectionsAnalyticsRetentionLevelDetailPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/retention_level/:materialCode/drill_sections/:drillSectionPublicId"
          element={<SectionsAnalyticsDrillSectionDetailPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/study_task"
          element={<SectionsAnalyticsStudyTaskPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/study_task/new"
          element={<SectionsAnalyticsStudyTaskNewPage />}
        />
        <Route
          path="/sections/:sectionId/analytics/study_task/:studyTaskId"
          element={<SectionsAnalyticsStudyTaskDetailPage />}
        />
        <Route
          path="/sections/:sectionId/kartes"
          element={<SectionsKartesPage />}
        />
        <Route
          path="/sections/:sectionId/student_follow_summaries"
          element={<SectionsStudentFollowSummariesPage />}
        />
        <Route
          path="/sections/:sectionId/student_follow_summaries/prompt"
          element={<SectionsStudentFollowPromptPage />}
        />
        {/* 学習コーチング。将来のタブ（申込一覧／面談一覧）を前提に、申込一覧用のパスを切る */}
        <Route
          path="/sections/:sectionId/learning_coach/application_requests"
          element={<SectionsLearningCoachApplicationRequestsPage />}
        />
        <Route
          path="/sections/:sectionId/message_threads"
          element={<SectionsMessageThreadsPage />}
        />
        <Route
          path="/sections/:sectionId/tags/messages"
          element={<SectionsAnnounceMessagesPage />}
        />
        <Route
          path="/sections/:sectionId/staff_board_posts"
          element={<SectionsStaffBoardPostsPage />}
        />
        <Route
          path="/sections/:sectionId/staff_board_posts/:staffBoardPostId"
          element={<SectionsStaffBoardPostPage />}
        />
        <Route
          path="/sections/:sectionId/staff_board_posts/:staffBoardPostId/edit"
          element={<SectionsStaffBoardPostEditPage />}
        />
        <Route
          // NOTE:
          // /sections/:sectionId/tags/messagesと
          // /sections/:sectionId/tags/messages/newで
          // 振る舞いを変えたいのでmatch.params.actionとして受け取っている
          path="/sections/:sectionId/tags/messages/:action"
          element={<SectionsAnnounceMessagesPage />}
        />
        <Route
          path="/sections/:sectionId/tags/:destinationTagId/messages"
          element={<SectionsAnnounceMessagesPage />}
        />
        <Route
          path="/sections/:sectionId/study_records/comments"
          element={<SectionStudyRecordCommentsPage />}
        />
        <Route
          path="/sections/:sectionId/guardian_message_threads"
          element={<SectionsGuardianMessageThreadsPage />}
        />
        <Route
          path="/sections/:sectionId/tags/guardian_messages"
          element={<SectionsGuardianAnnounceMessagesPage />}
        />
        <Route
          path="/sections/:sectionId/tags/guardian_messages/:action"
          element={<SectionsGuardianAnnounceMessagesPage />}
        />
        <Route
          path="/sections/:sectionId/tags/:tagId/guardian_messages"
          element={<SectionsGuardianAnnounceMessagesPage />}
        />
        <Route
          path="/sections/:sectionId/study_schedules"
          element={<SectionsStudySchedules />}
        />
        <Route
          path="/sections/:sectionId/schedules/list"
          element={<SectionsSettingsSectionScheduleListPage />}
        />
        <Route
          path="/sections/:sectionId/lecture_sessions/:lectureSessionId/attendees"
          element={<SectionsLectureSessionDetailPage />}
        />
        <Route
          path="/sections/:sectionId/lecture_sessions/:lectureSessionId/questionnaires"
          element={<SectionsLectureSessionDetailPage />}
        />
        <Route
          path="/sections/:sectionId/lecture_transfers/new"
          element={<SectionsLectureTransfersNewPage />}
        />
      </Route>
      <Route element={<StudentLayout />}>
        <Route path="/students/:studentId" element={<StudentsTimelinePage />} />
        <Route
          path="/students/:studentId/timeline"
          element={<StudentsTimelinePage />}
        />
        <Route
          path="/students/:studentId/analytics"
          element={<StudentsAnalyticsTimePage />}
        />
        <Route
          path="/students/:studentId/analytics/time"
          element={<StudentsAnalyticsTimePage />}
        />
        <Route
          path="/students/:studentId/analytics/retention_level"
          element={<StudentsAnalyticsRetentionLevelPage />}
        />
        <Route
          path="/students/:studentId/analytics/examination"
          element={<StudentsAnalyticsExaminationPage />}
        />
        <Route
          path="/students/:studentId/analytics/examinations/:examinationId/result"
          element={<StudentAnalyticsExaminationResultPage />}
        />
        <Route
          path="/students/:studentId/analytics/lecture_attendance"
          element={<StudentsAnalyticsLectureAttendancePage />}
        />
        <Route
          path="/students/:studentId/learning_material_tag_analytics/time"
          element={<StudentsLearningMaterialTagAnalyticsTimePage />}
        />
        <Route
          path="/students/:studentId/analytics/stay"
          element={<StudentsStayAnalyticsPage />}
        />
        <Route
          path="/students/:studentId/kartes"
          element={<StudentsKartesPage />}
        />
        <Route
          path="/students/:studentId/kartes/:karteId"
          element={<StudentsKartePage />}
        />
        <Route
          path="/students/:studentId/kartes/:karteId/edit"
          element={<StudentsKarteEditPage />}
        />
        <Route
          path="/students/:studentId/study_records/:studyRecordId"
          element={<StudentsStudyRecordPage />}
        />
        <Route
          path="/students/:studentId/analytics/amount"
          element={<StudentsAnalyticsAmountPage />}
        />
        <Route
          path="/students/:studentId/messages"
          element={<StudentsMessagesPage />}
        />
        <Route
          path="/students/:studentId/guardian_messages"
          element={<StudentsGuardianMessagesPage />}
        />
        <Route
          path="/students/:studentId/learning_materials"
          element={<StudentsLearningMaterialsPage />}
        />
        <Route
          path="/students/:studentId/learning_materials/new"
          element={<StudentsLearningMaterialsNewPage />}
        />
        <Route
          path="/students/:studentId/learning_materials/pre_registrations"
          element={<StudentsLearningMaterialsPreRegistrationsPage />}
        />
        <Route
          path="/students/:studentId/learning_materials/pre_registrations/new"
          element={<StudentsLearningMaterialsPreRegistrationsNewPage />}
        />
        <Route
          path="/students/:studentId/calendar"
          element={<StudentsCalendar />}
        />
        <Route
          path="/students/:studentId/study_schedules"
          element={<StudentsStudySchedulesPage />}
        />
        <Route
          path="/students/:studentId/content_courses/:courseId/results"
          element={<StudentCourseResultPage />}
        />
        <Route
          path="/students/:studentId/analytics/examinations/result/new"
          element={<StudentsExaminationResultsNewPage />}
        />
        <Route
          path="/students/:studentId/analytics/examinations/:examinationId/result/edit"
          element={<StudentsExaminationResultEditPage />}
        />
      </Route>
      {/* settings/ */}
      <Route path="/settings/profile" element={<SettingsProfilePage />} />
      <Route path="/settings/email/:token" element={<SettingsEmailPage />} />
      <Route element={<SectionSettingLayout />}>
        <Route
          path="/sections/:sectionId/students/new"
          element={<SectionsSettingsStudentsNewPage />}
        />
        <Route
          path="/sections/:sectionId/settings/learning_material_tags"
          element={<SectionsSettingsLearningMaterialTags />}
        />
        <Route
          path="/sections/:sectionId/settings/management_learning_materials"
          element={<SectionsSettingsManagementLearningMaterialsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/digital_learning_materials"
          element={<SectionsSettingsDigitalLearningMaterialsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/digital_learning_materials/new"
          element={<SectionsSettingsDigitalLearningMaterialsNewPage />}
        />

        {/* 新しい校舎独自教材ページ */}
        <Route
          path="/sections/:sectionId/settings/section_learning_materials"
          element={<SectionsSettingsSectionLearningMaterialsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/section_learning_materials/new"
          element={<SectionsSettingsSectionLearningMaterialsFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/section_learning_materials/:learningMaterialId/edit"
          element={<SectionsSettingsSectionLearningMaterialsFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/students"
          element={<SectionsSettingsStudentsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/operators"
          element={<SectionsSettingsOperatorsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/karte_templates"
          element={<SectionsSettingsKarteTemplatesPage />}
        />
        <Route
          path="/sections/:sectionId/settings/ai_assistant_templates"
          element={<SectionsSettingsAiAssistantTemplatesPage />}
        />
        <Route
          path="/sections/:sectionId/settings/ai_assistant_templates/new"
          element={<SectionsSettingsAiAssistantTemplatesNewPage />}
        />
        <Route
          path="/sections/:sectionId/settings/ai_assistant_templates/:id/edit"
          element={<SectionsSettingsAiAssistantTemplatesEditPage />}
        />
        <Route
          path="/sections/:sectionId/settings/karte_templates/new"
          element={<SectionsSettingsKarteTemplatesNewPage />}
        />
        <Route
          path="/sections/:sectionId/settings/karte_templates/:karteTemplateId/edit"
          element={<SectionsSettingsKarteTemplatesEditPage />}
        />
        <Route
          path="/sections/:sectionId/settings/stays"
          element={<SectionsSettingsStaysPage />}
        />
        <Route
          path="/sections/:sectionId/settings/stays/staying_students"
          element={<SectionsSettingsStayingStudentsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/stays/details"
          element={<SectionsSettingsStaysDetailsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/tags"
          element={<SectionsSettingsTagsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/guardian_message"
          element={<SectionsSettingsGuardianMessagePage />}
        />
        <Route
          path="/sections/:sectionId/settings/applications"
          element={<SectionsSettingsThirdPartyApplicationsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/applications/google_classroom"
          element={<SectionsSettingsGoogleClassroomPage />}
        />
        <Route
          path="/sections/:sectionId/settings/:importOperation"
          element={<SectionsSettingsImportOperationPage />}
        />
        <Route
          path="/sections/:sectionId/settings/import_histories"
          element={<SectionsSettingsImportHistoriesPage />}
        />
        <Route
          path="/sections/:sectionId/settings/import_histories/operations"
          element={<SectionsSettingsImportHistoriesOperationsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/import_histories/:importHistoryId"
          element={<SectionsSettingsImportHistoryPage />}
        />
        <Route
          path="/sections/:sectionId/settings/skills"
          element={<SectionsSettingsSkillsPage />}
        />
        <Route
          path={`/sections/:sectionId/settings/skill_masters`}
          element={<SectionsSettingsSkillMastersPage />}
        />
        <Route
          path={`/sections/:sectionId/settings/skill_masters/:id/skills/new`}
          element={<SectionsSettingsSkillsNewPage />}
        />
        <Route
          path={`/sections/:sectionId/settings/skills/:skillId/edit`}
          element={<SectionsSettingsSkillsEditPage />}
        />
        <Route
          path="/sections/:sectionId/settings/contents"
          element={<SectionsSettingsContentsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses"
          element={<SectionsSettingsContentCoursesPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/results"
          element={<SectionsSettingsContentCoursesResultsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/:contentCourseId/results"
          element={<SectionsSettingsContentUnitsResultsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/:contentCourseId/units/:unitId/results"
          element={<SectionsSettingsContentContentsResultsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/:contentCourseId/students"
          element={<SectionsSettingsContentCourseStudentsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/new"
          element={<SectionsSettingsContentCoursesNewOrEditPage action="new" />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/:courseId/edit"
          element={
            <SectionsSettingsContentCoursesNewOrEditPage action="edit" />
          }
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/share"
          element={<SectionsSettingsContentCoursesSharePage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/:contentCourseId/students"
          element={<SectionsSettingsContentCourseStudentsPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_courses/:courseId"
          element={<SectionsSettingsContentCourseDetail />}
        />
        <Route
          path="/sections/:sectionId/settings/content_videos/new"
          element={<SectionsSettingsContentVideosNewOrEditPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_videos/:contentId/edit"
          element={<SectionsSettingsContentVideosNewOrEditPage />}
        />
        <Route
          path="/sections/:sectionId/settings/content_files/new"
          element={<SectionSettingsNewContentFiles />}
        />
        <Route
          path="/sections/:sectionId/settings/content_files/:contentId/edit"
          element={<SectionSettingsEditContentFiles />}
        />
        <Route
          path="/sections/:sectionId/settings/teachers"
          element={<SectionsSettingsTeachersPage />}
        >
          <Route
            path=":archiveStatus"
            element={<SectionsSettingsTeachersPage />}
          />
        </Route>
        <Route
          path="/sections/:sectionId/settings/teachers/new"
          element={<SectionsSettingsTeacherNewPage />}
        />
        <Route
          path="/sections/:sectionId/settings/classrooms"
          element={<SectionsSettingsClassroomPage />}
        >
          <Route
            path=":archiveStatus"
            element={<SectionsSettingsClassroomPage />}
          />
        </Route>
        <Route
          path="/sections/:sectionId/settings/classrooms/new"
          element={<SectionsSettingsClassroomFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/classrooms/:classroomId/edit"
          element={<SectionsSettingsClassroomFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/classrooms/:classroomId/copy"
          element={<SectionsSettingsClassroomFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/lectures/new"
          element={<SectionsSettingsLecturesFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/lectures/:lectureId/edit"
          element={<SectionsSettingsLecturesFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/lectures/:lectureId/copy"
          element={<SectionsSettingsLecturesFormPage />}
        />
        <Route
          path="/sections/:sectionId/settings/lectures"
          element={<SectionsSettingsLecturesPage />}
        >
          <Route
            path=":archiveStatus"
            element={<SectionsSettingsLecturesPage />}
          />
        </Route>
      </Route>
    </Route>

    {/* NOTE: 完全一致でマッチするpathがないときにここへルーティング */}
    <Route path="*" element={<NoRouteMatch />} />
  </Routes>
);

const Root = (props: Props): React.ReactElement<Props> => {
  return (
    <Provider store={props.store}>
      <FSErrorBoundary>
        <PersistGate
          loading={
            <div
              style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader loading={true} />
            </div>
          }
          persistor={props.persistor}
        >
          <IntercomContainer />
          <FlashMessage />
          <WindowReload />
          <Tracker />
          <ErrorTracking />
          <LoginCallback />
          <FSFeatureProviders>
            <FSRoutes />
          </FSFeatureProviders>
        </PersistGate>
      </FSErrorBoundary>
    </Provider>
  );
};

const providers = [
  NavigationProvider,
  StudentFilterProvider,
  SingleDateFilterProvider,
  LearningMaterialFilterWithLocalStorageProvider,
  LearningMaterialFilterProvider,
  SectionAnalyticsStudyTaskTabsProvider,
  FieldVisibilityToggleProvider,
  DrawerProvider,
];

export const FSFeatureProviders = composeProviders(providers);

export default Root;
